/**
 * The month card's summary (OH-195, spec 2026-10-21 §5.3): the base P&L in
 * groups with subtotals and a Result, and the corrections block — late lines
 * and recognized deltas — whose sum takes the Result to Σ full_pnl.
 *
 * A snapshot's version decides everything here: v2 (`version: 2`) carries its
 * late lines on their own; v1 (no `version`) has them only inside full_pnl
 * and they are reconstructed per key (I6, ENV-04). Never infer the version
 * from which arrays happen to be present.
 */
import type { FinanceCategory, FinancePnLLine, FinanceRecognizedDelta, FinanceSnapshot } from '@/api/generated/types.gen';

export type PnLGroupKey = 'income' | 'channel_fees' | 'opex' | 'capex' | 'fx' | 'legacy_financing';
export interface PnLGroup {
  key: PnLGroupKey;
  title: string;
  lines: FinancePnLLine[];
  subtotal: number;
}

// OH-216: Capital expenses come last, so everything above them adds up to the
// result before capital expenses (spec §5.3 had them before FX).
const GROUPS: readonly { key: PnLGroupKey; title: string }[] = [
  { key: 'income', title: 'Income' },
  { key: 'channel_fees', title: 'Channel fees' },
  { key: 'opex', title: 'Operating expenses' },
  { key: 'fx', title: 'FX' },
  // Plan 2026-10-21 decision 14: only a v1 snapshot can hold a category line of type
  // financing (v2 keeps financing out of P&L, spec §5.1). It is neither an
  // operating expense nor income, so it gets its own group, shown only when
  // such lines exist.
  { key: 'legacy_financing', title: 'Financing (counted in P&L before 2026-10)' },
  { key: 'capex', title: 'Capital expenses' },
];

export const isV2 = (s: FinanceSnapshot): boolean => (s.version ?? 0) >= 2;

/** Spec 2026-10-21 §5.3 groups; plan 2026-10-21 decisions 9 and 14. */
export function groupOf(l: FinancePnLLine, cat: FinanceCategory | undefined): PnLGroupKey {
  switch (l.dimension) {
    case 'revenue':
      return 'income';
    case 'ota_commission':
    case 'withheld':
      return 'channel_fees';
    case 'fx':
      return 'fx';
    default:
      if (l.expense_type === 'financing') return 'legacy_financing';
      // Every kind but `expense` is an income-side category; a category
      // missing from the loaded directory goes by its sign.
      if (cat ? cat.kind !== 'expense' : l.amount_eur_minor >= 0) return 'income';
      return l.expense_type === 'capital' ? 'capex' : 'opex';
  }
}

/**
 * resultBeforeCapex (OH-216) is the result less the Capital expenses
 * subtotal; null when the month has none, since it would repeat the result.
 */
export function groupPnL(
  lines: readonly FinancePnLLine[],
  categoriesById: Record<string, FinanceCategory>,
): { groups: PnLGroup[]; result: number; resultBeforeCapex: number | null } {
  const groups: PnLGroup[] = [];
  let result = 0;
  for (const g of GROUPS) {
    const ls = lines.filter((l) => groupOf(l, l.category_id ? categoriesById[l.category_id] : undefined) === g.key);
    if (ls.length === 0) continue;
    const subtotal = ls.reduce((s, l) => s + l.amount_eur_minor, 0);
    groups.push({ key: g.key, title: g.title, lines: ls, subtotal });
    result += subtotal;
  }
  const capex = groups.find((g) => g.key === 'capex');
  return { groups, result, resultBeforeCapex: capex ? result - capex.subtotal : null };
}

type Keyed = Pick<FinancePnLLine, 'dimension' | 'apartment_id' | 'category_id' | 'expense_type' | 'amount_eur_minor'>;
const keyOf = (l: Keyed) => [l.dimension, l.apartment_id ?? '', l.category_id ?? '', l.expense_type ?? ''].join('|');

class Corrupt extends Error {}
function big(n: unknown): bigint {
  if (typeof n !== 'number' || !Number.isSafeInteger(n)) throw new Corrupt();
  return BigInt(n);
}
const sum = (ls: readonly Keyed[]) => ls.reduce((s, l) => s + big(l.amount_eur_minor), 0n);
const toNumber = (b: bigint) => {
  if (b > BigInt(Number.MAX_SAFE_INTEGER) || b < BigInt(Number.MIN_SAFE_INTEGER)) throw new Corrupt();
  return Number(b);
};

/**
 * Why the card shows a snapshot's stored full_pnl whole instead of base +
 * corrections. non_integer_amount: an amount, or a sum of them, is not a safe
 * integer, so the parts cannot be added exactly. parts_disagree: the parts add
 * up, but base + corrections ≠ full.
 */
export type StoredFullReason = 'parts_disagree' | 'non_integer_amount';

/** The line the card shows above the stored full P&L, one per reason. */
export const STORED_FULL_REASON: Record<StoredFullReason, string> = {
  parts_disagree: "This snapshot's stored parts don't add up — showing the stored full P&L as is.",
  non_integer_amount: "This snapshot holds an amount that isn't a whole number of cents — showing the stored full P&L as is.",
};

/**
 * I6 / ENV-04: a v1 snapshot holds full = base + late + recognized but no late
 * lines of its own. late(k) = full(k) − base(k) − Σ recognized(k) over the
 * union of keys, a missing key being 0, in BigInt. {ok:false} for any amount
 * that is not a safe integer — the card then shows the stored full_pnl whole
 * (plan 2026-10-21 decision 10).
 */
export function reconstructV1Late(
  s: FinanceSnapshot,
): { ok: true; lines: FinancePnLLine[] } | { ok: false; reason: StoredFullReason } {
  try {
    const acc = new Map<string, { line: Keyed; v: bigint }>();
    const add = (l: Keyed, sign: bigint) => {
      const k = keyOf(l);
      const e = acc.get(k) ?? { line: l, v: 0n };
      e.v += sign * big(l.amount_eur_minor);
      acc.set(k, e);
    };
    s.full_pnl.forEach((l) => add(l, 1n));
    s.base_pnl.forEach((l) => add(l, -1n));
    s.recognized_deltas.forEach((d) => add(d, -1n));
    const lines: FinancePnLLine[] = [];
    for (const { line, v } of acc.values()) {
      if (v === 0n) continue;
      lines.push({
        dimension: line.dimension,
        apartment_id: line.apartment_id,
        category_id: line.category_id,
        expense_type: line.expense_type,
        amount_eur_minor: toNumber(v),
      });
    }
    if (sum(lines) + sum(s.recognized_deltas) + sum(s.base_pnl) !== sum(s.full_pnl)) {
      return { ok: false, reason: 'parts_disagree' };
    }
    return { ok: true, lines };
  } catch (e) {
    if (e instanceof Corrupt) return { ok: false, reason: 'non_integer_amount' };
    throw e;
  }
}

export interface CorrectionRow {
  line: FinancePnLLine;
  label: string;
  sourceMonth: string | null;
}
export type Corrections =
  | { kind: 'none' }
  | { kind: 'rows'; rows: CorrectionRow[]; total: number; fullResult: number }
  /** fullResult is NaN when the stored amounts do not even sum (the card then omits the result row). */
  | { kind: 'stored_full'; reason: StoredFullReason; lines: FinancePnLLine[]; fullResult: number };

const recognizedRow = (d: FinanceRecognizedDelta): CorrectionRow => ({
  line: d,
  label: `Recognized against ${d.source_month.slice(0, 7)}`,
  sourceMonth: d.source_month,
});

/** Spec §5.3 «Corrections»: shown only when there are any; the arithmetic is checked. */
export function correctionsOf(s: FinanceSnapshot): Corrections {
  try {
    const fullResult = toNumber(sum(s.full_pnl));
    let late: FinancePnLLine[];
    let lateLabel: string;
    if (isV2(s)) {
      late = s.late_pnl ?? [];
      lateLabel = 'Late correction';
    } else {
      const r = reconstructV1Late(s);
      if (!r.ok) return { kind: 'stored_full', reason: r.reason, lines: s.full_pnl, fullResult };
      late = r.lines;
      lateLabel = 'Late correction (source month not kept in older snapshots)';
    }
    const rows: CorrectionRow[] = [
      ...late.map((l) => ({ line: l, label: lateLabel, sourceMonth: null })),
      ...s.recognized_deltas.map(recognizedRow),
    ];
    const total = sum(rows.map((r) => r.line));
    // Unreachable for a v1 reconstruction (it closes by construction); for v2
    // it catches a snapshot whose stored parts disagree with its full_pnl —
    // checked before the empty case below, since a v2 snapshot with no late
    // lines and no recognized deltas must still have base = full.
    if (total + sum(s.base_pnl) !== sum(s.full_pnl)) {
      return { kind: 'stored_full', reason: 'parts_disagree', lines: s.full_pnl, fullResult };
    }
    if (rows.length === 0) return { kind: 'none' };
    return { kind: 'rows', rows, total: toNumber(total), fullResult };
  } catch (e) {
    if (e instanceof Corrupt) {
      return { kind: 'stored_full', reason: 'non_integer_amount', lines: s.full_pnl, fullResult: safeFullResult(s) };
    }
    throw e;
  }
}

/** Σ full_pnl when its own amounts are sound (the corrupt amount may sit elsewhere), NaN otherwise. */
function safeFullResult(s: FinanceSnapshot): number {
  try {
    return toNumber(sum(s.full_pnl));
  } catch (e) {
    if (e instanceof Corrupt) return Number.NaN;
    throw e;
  }
}
